import { EventPageFragment, EventPageType, PhotoXPosition, PhotoYPosition } from '@graphql/generated';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuxFrameContainer, AuxFrameInnerContainer } from './AuxFrame.styles';
import AuxFramePhotoSlider from './components/AuxFramePhotoSlider';
import AuxFrameTitle from './components/AuxFrameTitle';
import { EditPhoto } from './components/EditPhoto';
import { AuxPhoto } from './AuxFrame.types';
import { isInIframe } from '@shared/utils/isInIframe';
import { zeroRightClassName } from 'react-remove-scroll-bar';

interface IAuxFrameContext {
  eventId: string;
  page: Maybe<EventPageType>;
  photo?: AuxPhoto | null;
  setPageInView: (pageSlug: string) => void;
  previewFocusPoint: (alignX: PhotoXPosition, alignY: PhotoYPosition) => void;
}
export const AuxFrameContext = React.createContext<IAuxFrameContext>({
  eventId: '',
  page: EventPageType.welcome,
  setPageInView: () => {
    throw Error(`No AuxFrameContext`);
  },
  previewFocusPoint: (alignX, alignY) => {
    throw Error(`No AuxFrameContext`);
  }
});

export const AuxFrameProvider: React.FC<{ isSmallScreen: boolean; eventId: string; primaryPhoto?: AuxPhoto | null; pages: Array<EventPageFragment>; isPreviewing: boolean }> = ({
  isSmallScreen,
  eventId,
  primaryPhoto,
  pages,
  children,
  isPreviewing
}) => {
  const [currentPhoto, setCurrentPhoto] = useState<AuxPhoto | undefined | null>(primaryPhoto);
  const [currentPage, setCurrentPage] = useState<EventPageFragment | null | undefined>();
  const previewFocusPoint = (alignX: PhotoXPosition, alignY: PhotoYPosition) => {
    if (currentPhoto) {
      setCurrentPhoto({ ...currentPhoto, layout: { alignX, alignY } });
    }
  };
  const setPageInView = useCallback(
    (pageSlug: string) => {
      const page = pages.find(page => page.pageSlug === pageSlug);
      if (currentPage?.id !== page?.id) {
        setCurrentPage(page);
      }
    },
    [currentPage?.id, pages]
  );

  useEffect(() => {
    if (isSmallScreen) return;

    // Only change photo for desktop when currentPage changes
    setCurrentPhoto(getPhotoToApply());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryPhoto, pages, currentPage, isSmallScreen]);

  useEffect(() => {
    // Only change photo when a page is changed if (isPreviewing) is active.
    // One of the reasons the page can be changed is if we edit the photo.

    if (!isPreviewing) return;
    setCurrentPhoto(getPhotoToApply());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, isPreviewing]);

  const getPhotoToApply = () => {
    // photo will exist coming from the preview listener (angular app publishes data changes)
    // but the url will be null when there isn't a photo for that page
    // the fallback is the event photo, here that is primaryPhoto
    const pageForCurrentPage = (currentPage?.id && pages.find(p => p.id === currentPage.id)) || null;
    const photoToApply = pageForCurrentPage?.photo?.url ? pageForCurrentPage?.photo : primaryPhoto;
    return photoToApply;
  };

  return (
    <AuxFrameContext.Provider value={{ eventId, page: currentPage?.type || EventPageType.welcome, photo: currentPhoto, setPageInView, previewFocusPoint }}>
      {children}
    </AuxFrameContext.Provider>
  );
};

export const AuxFrame: React.FC<{
  title?: Maybe<string>;
  subtitle?: Maybe<string>;
  eventHandle: string;
  telemetryPhotoEditor: {
    onEditPhotoOpenedChange: (opened: boolean) => void;
    onChangePhotoButtonClicked: () => void;
    onFocusPointChange: (lastPoint: unknown, newPoint: unknown) => void;
    onFocusPointChangeError: (error: unknown) => void;
  };
}> = ({ title, subtitle, eventHandle, telemetryPhotoEditor, children }) => {
  const { photo } = useContext(AuxFrameContext);
  const isPreviewing = isInIframe();
  const { onEditPhotoOpenedChange, onChangePhotoButtonClicked, onFocusPointChange, onFocusPointChangeError } = telemetryPhotoEditor;

  return (
    <AuxFrameContainer className={zeroRightClassName}>
      <AuxFrameInnerContainer>
        {photo?.url && <AuxFramePhotoSlider photo={photo} />}
        <AuxFrameTitle x={'left'} y={'bottom'} textLight={true} title={title} subtitle={subtitle} />
        {children}
        {!isPreviewing && (
          <EditPhoto
            onFocusPointChangeError={onFocusPointChangeError}
            onFocusPointChange={onFocusPointChange}
            onChangePhotoButtonClicked={onChangePhotoButtonClicked}
            onEditPhotoOpenedChange={onEditPhotoOpenedChange}
            eventHandle={eventHandle}
          />
        )}
      </AuxFrameInnerContainer>
    </AuxFrameContainer>
  );
};
